import type {
  BaseTabsProps,
  TabOptions,
} from '@/components/generics/base/BaseTabs.vue'
import { getQueryParam } from '@/hooks/useQueryParam'
import { snakeCase } from 'lodash-es'
import { computed, ref, type Ref } from 'vue'
import { useRouter } from 'vue-router/composables'

interface UseTabOptionsParams {
  options: Ref<TabOptions[]>
  keepTabs: Ref<BaseTabsProps['keepTabs']>
  initialTabIndex: Ref<number>
  /** When defined, wins over the internally tracked index (controlled mode) */
  activeTabIndex?: Ref<number | undefined>
}

/**
 * Composable for managing tab options state and interactions.
 * Handles active tab tracking, filtering visible tabs, and query param initialization.
 *
 * @param params - Configuration including options, initial active tab, and keepTabs flag (all should be refs)
 * @returns Object with tab state, computed values, and tab selection method
 */
export const useTabOptions = (params: UseTabOptionsParams) => {
  const router = useRouter()

  const {
    options,
    keepTabs,
    initialTabIndex,
    activeTabIndex: controlledTabIndex,
  } = params

  const internalTabIndex = ref(initialTabIndex.value)

  const activeTabIndex = computed(
    () => controlledTabIndex?.value ?? internalTabIndex.value
  )

  const allTabOptions = computed(() => {
    return options.value.filter((option: TabOptions) => !option.hideTab)
  })

  const activeTabOption = computed(() => {
    return allTabOptions.value[activeTabIndex.value]
  })

  const tab = computed(() => {
    return {
      tab: activeTabOption.value?.value,
      keep: keepTabs.value,
    }
  })

  const isTabActive = (index: number): boolean => {
    return activeTabIndex.value === index
  }

  const setActiveTab = (index: number): void => {
    internalTabIndex.value = index
  }

  if (keepTabs.value) {
    const tabQueryParam = getQueryParam(Object.keys(tab.value)[0], router)

    if (tabQueryParam) {
      const tabIndex = allTabOptions.value.findIndex((option) => {
        if (typeof tabQueryParam === 'number') {
          return option.value === tabQueryParam
        }
        return snakeCase(option.value) === tabQueryParam
      })

      if (tabIndex !== -1) {
        internalTabIndex.value = tabIndex
      }
    }
  }

  return {
    activeTabIndex,
    activeTabOption,
    allTabOptions,
    tab,
    isTabActive,
    setActiveTab,
  }
}
